import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  Input,
  Spinner,
  Switch,
  Table,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  LayoutGridIcon,
  PencilIcon,
  PlusIcon,
  RefreshCwIcon,
  ShieldAlertIcon,
  TrashIcon,
  XIcon,
} from 'lucide-react'
import { useState } from 'react'
import { PageHeader } from '~/components/shell'
import { useAuth } from '~/contexts/AuthContext'
import {
  type CreateFlowTemplateRequest,
  type FlowTemplate,
  flowTemplatesApi,
  type UpdateFlowTemplateRequest,
} from '~/lib/api'
import { toaster } from '~/toaster'

export const Route = createFileRoute('/_app/admin/flow-templates')({
  component: FlowTemplatesPage,
})

function CreateTemplateModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void
  onSuccess: () => void
}) {
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [description, setDescription] = useState('')
  const [industry, setIndustry] = useState('')
  const [displayOrder, setDisplayOrder] = useState('0')

  const createMutation = useMutation({
    mutationFn: (data: CreateFlowTemplateRequest) => flowTemplatesApi.create(data),
    onSuccess: () => {
      toaster.create({
        title: 'Template created',
        type: 'success',
      })
      onSuccess()
      onClose()
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to create template',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!slug || !name) {
      toaster.create({
        title: 'Missing required fields',
        description: 'Name and slug are required',
        type: 'error',
      })
      return
    }

    // Validate slug format
    if (!/^[a-z0-9-]+$/.test(slug)) {
      toaster.create({
        title: 'Invalid slug',
        description: 'Slug must contain only lowercase letters, numbers, and hyphens',
        type: 'error',
      })
      return
    }

    createMutation.mutate({
      name,
      slug,
      description: description || undefined,
      industry: industry || undefined,
      nodes: [],
      edges: [],
      displayOrder: parseInt(displayOrder) || 0,
    })
  }

  return (
    <Box
      position="fixed"
      inset={0}
      bg="blackAlpha.600"
      zIndex={1000}
      onClick={onClose}
      overflow="auto"
    >
      <Box
        position="relative"
        mx="auto"
        my={8}
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="500px"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <Heading size="md">Create New Template</Heading>
          <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close dialog">
            <XIcon size={16} />
          </Button>
        </Flex>

        <Box p={6}>
          <form onSubmit={handleSubmit}>
            <VStack gap={4} align="stretch">
              <HStack gap={4}>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Name *
                  </Text>
                  <Input
                    placeholder="Factory Architecture"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </Box>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Slug *
                  </Text>
                  <Input
                    placeholder="factory"
                    value={slug}
                    onChange={(e) =>
                      setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                    }
                    required
                  />
                </Box>
              </HStack>

              <HStack gap={4}>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Industry
                  </Text>
                  <Input
                    placeholder="Manufacturing"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                  />
                </Box>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Display Order
                  </Text>
                  <Input
                    type="number"
                    placeholder="0"
                    value={displayOrder}
                    onChange={(e) => setDisplayOrder(e.target.value)}
                  />
                </Box>
              </HStack>

              <Box>
                <Text fontWeight="medium" mb={1} fontSize="sm">
                  Description
                </Text>
                <Textarea
                  placeholder="Brief description of the template..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                />
              </Box>

              <Text fontSize="xs" color="content.secondary">
                Note: Nodes and edges will be empty. Use the Playground to create the flow and then
                copy it here.
              </Text>

              <HStack gap={2} justify="flex-end" pt={4}>
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" colorPalette="blue" loading={createMutation.isPending}>
                  Create Template
                </Button>
              </HStack>
            </VStack>
          </form>
        </Box>
      </Box>
    </Box>
  )
}

function EditTemplateModal({
  template,
  onClose,
  onSuccess,
}: {
  template: FlowTemplate
  onClose: () => void
  onSuccess: () => void
}) {
  const [name, setName] = useState(template.name)
  const [slug, setSlug] = useState(template.slug)
  const [description, setDescription] = useState(template.description || '')
  const [industry, setIndustry] = useState(template.industry || '')
  const [displayOrder, setDisplayOrder] = useState(template.displayOrder.toString())
  const [isActive, setIsActive] = useState(template.isActive)
  const [nodesJson, setNodesJson] = useState(JSON.stringify(template.nodes, null, 2))
  const [edgesJson, setEdgesJson] = useState(JSON.stringify(template.edges, null, 2))
  const [showJsonEditor, setShowJsonEditor] = useState(false)

  const updateMutation = useMutation({
    mutationFn: (data: UpdateFlowTemplateRequest) => flowTemplatesApi.update(template.id, data),
    onSuccess: () => {
      toaster.create({
        title: 'Template updated',
        type: 'success',
      })
      onSuccess()
      onClose()
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to update template',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Validate slug format
    if (slug && !/^[a-z0-9-]+$/.test(slug)) {
      toaster.create({
        title: 'Invalid slug',
        description: 'Slug must contain only lowercase letters, numbers, and hyphens',
        type: 'error',
      })
      return
    }

    // Build update request - only include changed fields
    const updateData: UpdateFlowTemplateRequest = {}

    if (name !== template.name) updateData.name = name
    if (slug !== template.slug) updateData.slug = slug
    if (description !== (template.description || ''))
      updateData.description = description || undefined
    if (industry !== (template.industry || '')) updateData.industry = industry || undefined
    if (parseInt(displayOrder) !== template.displayOrder)
      updateData.displayOrder = parseInt(displayOrder)
    if (isActive !== template.isActive) updateData.isActive = isActive

    // Parse and validate JSON if edited
    if (showJsonEditor) {
      try {
        const parsedNodes = JSON.parse(nodesJson)
        const parsedEdges = JSON.parse(edgesJson)
        updateData.nodes = parsedNodes
        updateData.edges = parsedEdges
      } catch {
        toaster.create({
          title: 'Invalid JSON',
          description: 'Please check the nodes and edges JSON format',
          type: 'error',
        })
        return
      }
    }

    updateMutation.mutate(updateData)
  }

  return (
    <Box
      position="fixed"
      inset={0}
      bg="blackAlpha.600"
      zIndex={1000}
      onClick={onClose}
      overflow="auto"
    >
      <Box
        position="relative"
        mx="auto"
        my={8}
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW={showJsonEditor ? '900px' : '500px'}
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <Heading size="md">Edit Template: {template.name}</Heading>
          <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close dialog">
            <XIcon size={16} />
          </Button>
        </Flex>

        <Box p={6}>
          <form onSubmit={handleSubmit}>
            <VStack gap={4} align="stretch">
              <HStack gap={4}>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Name *
                  </Text>
                  <Input
                    placeholder="Factory Architecture"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </Box>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Slug *
                  </Text>
                  <Input
                    placeholder="factory"
                    value={slug}
                    onChange={(e) =>
                      setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                    }
                    required
                  />
                </Box>
              </HStack>

              <HStack gap={4}>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Industry
                  </Text>
                  <Input
                    placeholder="Manufacturing"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                  />
                </Box>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Display Order
                  </Text>
                  <Input
                    type="number"
                    placeholder="0"
                    value={displayOrder}
                    onChange={(e) => setDisplayOrder(e.target.value)}
                  />
                </Box>
              </HStack>

              <Box>
                <Text fontWeight="medium" mb={1} fontSize="sm">
                  Description
                </Text>
                <Textarea
                  placeholder="Brief description of the template..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                />
              </Box>

              <HStack gap={2}>
                <Switch.Root checked={isActive} onCheckedChange={(e) => setIsActive(e.checked)}>
                  <Switch.HiddenInput />
                  <Switch.Control />
                </Switch.Root>
                <Text fontSize="sm">Active</Text>
              </HStack>

              <Box borderTopWidth="1px" borderColor="border" pt={4}>
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontWeight="medium" fontSize="sm">
                    Nodes & Edges JSON
                  </Text>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => setShowJsonEditor(!showJsonEditor)}
                  >
                    {showJsonEditor ? 'Hide' : 'Edit'} JSON
                  </Button>
                </Flex>
                {!showJsonEditor && (
                  <Text fontSize="sm" color="content.secondary">
                    {template.nodes.length} nodes, {template.edges.length} edges
                  </Text>
                )}
              </Box>

              {showJsonEditor && (
                <HStack gap={4} align="stretch">
                  <Box flex={1}>
                    <Text fontWeight="medium" mb={1} fontSize="sm">
                      Nodes JSON
                    </Text>
                    <Textarea
                      fontFamily="mono"
                      fontSize="xs"
                      value={nodesJson}
                      onChange={(e) => setNodesJson(e.target.value)}
                      rows={15}
                    />
                  </Box>
                  <Box flex={1}>
                    <Text fontWeight="medium" mb={1} fontSize="sm">
                      Edges JSON
                    </Text>
                    <Textarea
                      fontFamily="mono"
                      fontSize="xs"
                      value={edgesJson}
                      onChange={(e) => setEdgesJson(e.target.value)}
                      rows={15}
                    />
                  </Box>
                </HStack>
              )}

              <HStack gap={2} justify="flex-end" pt={4}>
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" colorPalette="blue" loading={updateMutation.isPending}>
                  Save Changes
                </Button>
              </HStack>
            </VStack>
          </form>
        </Box>
      </Box>
    </Box>
  )
}

function FlowTemplatesPage() {
  const { hasMinRole } = useAuth()
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<FlowTemplate | null>(null)

  // Check if user has at least operator role
  const isOperatorOrAbove = hasMinRole('operator')

  // Redirect if not operator or above
  if (!isOperatorOrAbove) {
    return (
      <Flex h="100%" align="center" justify="center">
        <VStack gap={4} textAlign="center" p={8}>
          <Box p={4} borderRadius="full" bg="danger.subtle">
            <ShieldAlertIcon size={48} color="var(--chakra-colors-red-500)" />
          </Box>
          <Heading size="lg" color="danger.fg">
            Access Denied
          </Heading>
          <Text color="content.secondary" maxW="400px">
            This page is restricted to <strong>operator</strong> role or higher.
          </Text>
        </VStack>
      </Flex>
    )
  }

  const {
    data: templates,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['flow-templates', 'all'],
    queryFn: () => flowTemplatesApi.listAll(),
    retry: false,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => flowTemplatesApi.delete(id),
    onSuccess: () => {
      toaster.create({
        title: 'Template deleted',
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['flow-templates'] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to delete template',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleDelete = (template: FlowTemplate) => {
    if (confirm(`Are you sure you want to delete "${template.name}"?`)) {
      deleteMutation.mutate(template.id)
    }
  }

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        title="Flow Template Management"
        subtitle="Manage flow templates for industry use cases"
        actions={
          <>
            <Button size="sm" colorPalette="blue" onClick={() => setShowCreateModal(true)}>
              <PlusIcon size={16} />
              Add Template
            </Button>
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              <RefreshCwIcon size={16} />
              Refresh
            </Button>
          </>
        }
      />

      <VStack align="stretch" gap={6}>
        <Card.Root>
          <Table.Root>
            <Table.Header>
              <Table.Row bg="bg.subtle">
                <Table.ColumnHeader w="60px">
                  <LayoutGridIcon size={16} />
                </Table.ColumnHeader>
                <Table.ColumnHeader>Name</Table.ColumnHeader>
                <Table.ColumnHeader>Industry</Table.ColumnHeader>
                <Table.ColumnHeader w="100px">Nodes</Table.ColumnHeader>
                <Table.ColumnHeader w="100px">Edges</Table.ColumnHeader>
                <Table.ColumnHeader w="80px">Order</Table.ColumnHeader>
                <Table.ColumnHeader w="80px">Status</Table.ColumnHeader>
                <Table.ColumnHeader w="100px">Actions</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {isLoading ? (
                <Table.Row>
                  <Table.Cell colSpan={8}>
                    <Flex justify="center" py={8}>
                      <Spinner />
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ) : !templates || templates.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={8}>
                    <Text textAlign="center" py={8} color="content.secondary">
                      No templates found
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ) : (
                templates.map((template) => (
                  <Table.Row key={template.id}>
                    <Table.Cell>
                      <Box
                        w="40px"
                        h="40px"
                        bg="bg.subtle"
                        borderRadius="md"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <LayoutGridIcon size={20} color="var(--chakra-colors-gray-400)" />
                      </Box>
                    </Table.Cell>
                    <Table.Cell>
                      <VStack align="flex-start" gap={0}>
                        <Text fontWeight="medium">{template.name}</Text>
                        <Text fontSize="sm" color="content.secondary">
                          {template.slug}
                        </Text>
                        {template.description && (
                          <Text fontSize="xs" color="content.tertiary" maxW="300px" truncate>
                            {template.description}
                          </Text>
                        )}
                      </VStack>
                    </Table.Cell>
                    <Table.Cell>
                      <Text
                        fontSize="sm"
                        color={template.industry ? 'inherit' : 'content.tertiary'}
                      >
                        {template.industry || 'Not specified'}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">{template.nodes.length}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">{template.edges.length}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">{template.displayOrder}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge colorPalette={template.isActive ? 'green' : 'red'} variant="subtle">
                        {template.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <HStack gap={1}>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingTemplate(template)}
                          aria-label={`Edit ${template.name}`}
                        >
                          <PencilIcon size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          colorPalette="red"
                          onClick={() => handleDelete(template)}
                          aria-label={`Delete ${template.name}`}
                        >
                          <TrashIcon size={14} />
                        </Button>
                      </HStack>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </Card.Root>
      </VStack>

      {showCreateModal && (
        <CreateTemplateModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['flow-templates'] })}
        />
      )}

      {editingTemplate && (
        <EditTemplateModal
          template={editingTemplate}
          onClose={() => setEditingTemplate(null)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['flow-templates'] })}
        />
      )}
    </Box>
  )
}
